import EFinanceTypes from 'types/enums/EFinanceTypes';
import EFixedDownPaymentRates from 'types/enums/EFixedDownPaymentRates';
import EMachines from 'types/enums/EMachines';
import EPaymentForms from 'types/enums/EPaymentForms';
import {
  CalculatorFrequencyToIndexType,
  ICalculatorResult,
  ICalculatorVariables,
} from 'types/interfaces/ICalculator';
import { IApiOffer } from 'types/interfaces/IOffer';
import { SimulatorStateBase } from 'types/interfaces/ISimulatorState';

import checkForBalloon, {
  checkForInterestBasedBalloon,
} from './utils/checkForBalloon';
import checkForResidualValue from './utils/checkForResidualValue';
import getResultAmount from './utils/getResultAmount';

export default class Calculator {
  variables: ICalculatorVariables;

  frequencyToIndex: CalculatorFrequencyToIndexType;

  values: Required<SimulatorStateBase>;

  options: IApiOffer;

  optionsDefault: Partial<IApiOffer>;

  result: ICalculatorResult;

  variablesDefault: ICalculatorVariables;

  settings: {
    advancePaymentAllowed: boolean;
    isLeasing: boolean;
    downPaymentPercentage?: EFixedDownPaymentRates;
  };

  constructor() {
    // the frequency used to calculate the rates
    this.frequencyToIndex = {
      monthly: 1,
      quarterly: 2,
      semiannually: 3,
      annually: 4,
    };

    // default values / settings for the calculations
    this.values = {
      machinePrice: 0,
      balloon: 50,
      downPayment: 25,
      paymentForm: EPaymentForms.MONTHLY,
      operatingHours: 0,
      financeType: EFinanceTypes.LEASING,
      residualValue: 50,
      term: 3,
      upselling: false,
      advancePayments: 0,
      followingPayments: 0,
      machine: EMachines.MACHINE_1,
      interestRate: 0,
    };

    // available default options, can be changed in calculations
    this.optionsDefault = {
      loan_opening_fee: 0,
      loan_opening_fee_percent: 0,
      loan_opening_fee_type: 'currency',
      // number of rates and annual percentage rate
      loan_rates: {
        1: 0.49,
        2: 0.49,
        3: 0.99,
        4: 1.49,
        5: 1.99,
        6: 2.49,
        7: 2.49,
        8: 2.49,
        9: 2.49,
        10: 2.49,
      },
      // number of rates and annual percentage rate
      lease_rates: {
        1: 0.99,
        2: 0.99,
        3: 0.99,
        4: 0.99,
        5: 0.99,
        6: 0.99,
        7: 0.99,
        8: 0.99,
        9: 0.99,
        10: 0.99,
      },
      // number of rates and annual percentage rate
      upselling_rates: {
        1: 0.99,
        2: 0.99,
        3: 0.99,
        4: 0.99,
        5: 0.99,
        6: 0.99,
        7: 0.99,
        8: 0.99,
        9: 0.99,
        10: 0.99,
      },
    };

    // the calculated results will be stored here
    this.result = {
      hours: 0,
      hoursBalloon: 0,
      balloon: 0,
      result: 0,
      rate: 0,
      paymentsNumber: 0,
      downpayment: 0,
      residualValue: 0,
      openingFee: 0,
      openingFeeType: 'currency',
      showBalloon: 1,
      showRV: 1,
    };

    // the default values of the variables
    this.variablesDefault = {
      rate: 0,
      ratePerPeriod: 0,
      paymentsNumber: 0,
      downpayment: 0,
      amount: 0,
      futureValue: 0,
      balloon: 0,
      rv: 0,
      upselling: false,
    };

    this.variables = { ...this.variablesDefault };
  }

  /**
   * GO GO Calculation !
   *
   * @param options
   * @param values
   * @param {{advancePaymentAllowed: boolean; isLeasing: boolean, downPaymentPercentage: string}} settings Settings for exception logic
   * @returns {ICalculatorResult}
   */
  calculate(
    options: IApiOffer,
    values: SimulatorStateBase,
    settings: {
      advancePaymentAllowed: boolean;
      isLeasing: boolean;
      downPaymentPercentage?: EFixedDownPaymentRates;
    },
  ): ICalculatorResult {
    /**
     * We need the following console logs for creating new tests
     * The options, values and settings are used into "src/testData/index.ts" input test files
     * "src/testData/1.json", "src/testData/2.json" and so on
     */
    // console.log(options);
    // console.log(values);
    // console.log(settings);

    this.settings = settings;
    // copy the default variables
    this.variables = { ...this.variablesDefault };
    // set the defined options from backend
    this.setOptions(options);
    // set the provided values from simulator
    this.setValues(values);
    // calculate the rate depending on the upselling values
    this.calculateRate(values.upselling || false);
    // set the number of payments and rates per period
    this.calculatePaymentsNumber(this.settings.advancePaymentAllowed);

    // if balloon payment is found (pay remaining rest at end of contract)
    const balloonFound =
      checkForBalloon(this.options.balloon_period, this.values.term) ||
      checkForInterestBasedBalloon(
        this.options.interest_based_on_down_payment,
        this.values.machine,
      )
        ? 1
        : 0;

    const rvFound = checkForResidualValue(
      // eslint-disable-next-line no-underscore-dangle
      this.options._r_v_period,
      this.values.term,
    )
      ? 1
      : 0;

    // set the amount depending on down payment
    // @ts-ignore
    this.variables.amount = getResultAmount(
      this.values.machinePrice,
      this.values.downPayment,
    );

    this.variables.rv = this.values.residualValue;
    this.variables.balloon = this.values.balloon;

    if (this.settings.isLeasing) {
      // We use residual value as futureValue

      if (rvFound && this.options.country) {
        this.result.residualValue = this.values.residualValue;
        this.variables.futureValue = this.values.residualValue;
      }
    } else if (balloonFound && this.options.country) {
      // this should not happen if we'd pass the correct data (do not use ts-ignore!)
      // in some cases, this was undefined.
      if (typeof this.variables.balloon !== 'number') {
        this.variables.balloon = this.variablesDefault.balloon;
      }
      this.result.balloon = this.variables.balloon;
      this.variables.futureValue = this.variables.balloon;
    }

    if (!balloonFound || this.settings.isLeasing) {
      this.result.showBalloon = 0;
    } else {
      this.result.showBalloon = 1;
    }

    // show residual value depending on whether there is one or if leasing is used
    if (!rvFound || !this.settings.isLeasing) {
      this.result.showRV = 0;
    } else {
      this.result.showRV = 1;
    }

    this.variables.downpayment = this.values.downPayment;

    const firstPaymentAdvanceOption = this.options.first_payment_advance
      ? 1
      : 0;
    this.result.result = -Calculator.PMT(
      this.variables.ratePerPeriod,
      this.variables.paymentsNumber,
      this.variables.amount,
      -this.variables.futureValue,
      firstPaymentAdvanceOption,
    );

    // calculate opening fee
    if (this.options.loan_opening_fee_type === 'currency') {
      this.result.openingFee = this.options.loan_opening_fee;
    } else {
      this.result.openingFee =
        parseFloat(`${this.options.loan_opening_fee_percent}`) *
        parseFloat(`${this.variables.amount}`);
    }
    this.result.openingFeeType = this.options.loan_opening_fee_type;

    this.result.APR =
      Calculator.RATE(
        // @ts-ignore
        Calculator.getFrequencyNumber(this.values.paymentForm) * values.term,
        -this.result.result,
        this.variables.amount - this.result.openingFee,
        parseFloat((-this.variables.futureValue).toString()),
        firstPaymentAdvanceOption,
      ) *
      Calculator.getFrequencyNumber(this.values.paymentForm) *
      100;

    // Can be ignored as `term` is always between 1 and 10
    const loanRates = this.options.loan_rates[this.values.term];
    if (!this.settings.isLeasing && this.result.APR < loanRates) {
      this.result.APR = Number(loanRates);
    }

    // Can be ignored as `term` is always between 1 and 10
    const leaseRates = this.options.lease_rates[this.values.term];
    if (
      this.settings.isLeasing &&
      this.result.APR &&
      this.result.APR < leaseRates
    ) {
      this.result.APR = Number(leaseRates);
    }

    this.calculateHours();

    // take the value for rate from user when allow_user_preference_for_interest_rate is true
    if (this.options.allow_user_preference_for_interest_rate) {
      this.variables.rate = this.values.interestRate;
    }

    const result = this.formatResult(this.settings.advancePaymentAllowed);
    /**
     * We need the following console log for creating new tests
     * to help us compare the result with the one from "src/testData/index.ts"
     */
    // console.log('result', result);
    return result;
  }

  /**
   * Set values from simulator
   *
   * @param valuesArgs
   * @returns {SimulatorStateBase}
   */
  setValues(valuesArgs: SimulatorStateBase): SimulatorStateBase {
    // avoid overriding valid default values with undefined
    Object.keys(valuesArgs).forEach(key => {
      // @ts-ignore
      if (valuesArgs[key] === undefined) {
        // @ts-ignore
        // eslint-disable-next-line no-param-reassign
        delete valuesArgs[key];
      }
    });
    this.values = { ...this.values, ...valuesArgs };
    return this.values;
  }

  /**
   * Set options from backend
   *
   * @param optionsArgs
   *
   * @returns {IApiOffer}
   */
  setOptions(optionsArgs: IApiOffer): IApiOffer {
    this.options = { ...this.optionsDefault, ...optionsArgs };
    return this.options;
  }

  /**
   * Pick the good rate from options and set it in variables
   *
   * @param {boolean} upselling Extra service requested?
   */
  calculateRate(upselling: boolean): void {
    let rate;

    /**
     * Priorities:
     * - An extra service is requested: we choose the rates of the extra service, no matter the financing type.
     * - No extra service: we choose the rates of the correct financing type
     */
    if (upselling) {
      // @ts-ignore Can be ignored as `term` is always between 1 and 10
      rate = this.options.upselling_rates[this.values.term];
    } else if (
      this.options.interest_based_on_down_payment &&
      this.values.financeType === EFinanceTypes.PURCHASE
    ) {
      if (!this.settings.downPaymentPercentage) {
        // @Todo error handling: This should not be the case
        //  as the value gets set if interestBasedOnDownPayment exists in offer.
        console.error('downPaymentPercentage not set');
      }
      rate =
        // @ts-ignore Can be ignored as `term` is always between 1 and 10
        this.options.interest_based_on_down_payment.rates[this.values.term][
          this.settings.downPaymentPercentage || 0
        ][this.values.paymentForm];
    } else if (this.settings.isLeasing) {
      rate = this.options.lease_rates[this.values.term];
    } else {
      rate = this.options.loan_rates[this.values.term];
    }

    // take the value for rate from user when allow_user_preference_for_interest_rate is true
    this.variables.rate = this.options.allow_user_preference_for_interest_rate
      ? this.values.interestRate
      : Number(rate); // Has to be converted as rates from backend are currently in format e.g. "0.00"
  }

  /**
   * Sets paymentNumber and ratePerPeriod
   *
   * @param {boolean} allowAdvancePayment Enable advance payment logic?
   */
  calculatePaymentsNumber(allowAdvancePayment: boolean): void {
    if (
      allowAdvancePayment &&
      this.values.advancePayments &&
      this.values.followingPayments
    ) {
      this.variables.paymentsNumber =
        this.values.advancePayments + this.values.followingPayments;
    } else {
      this.variables.paymentsNumber =
        this.values.term *
        Calculator.getFrequencyNumber(this.values.paymentForm);
      if (
        !this.settings.isLeasing &&
        // @ts-ignore
        this.options.balloon_amount &&
        // @ts-ignore
        this.options.balloon_amount > 0
      ) {
        this.variables.paymentsNumber -= 1;
      }
    }
    this.variables.ratePerPeriod =
      (this.options.allow_user_preference_for_interest_rate
        ? this.values.interestRate
        : this.variables.rate) /
      100 /
      Calculator.getFrequencyNumber(this.values.paymentForm);
  }

  /**
   * Calculates hours and hoursBalloon
   */
  calculateHours(): void {
    // initialize frequency number
    let r = 0;

    if (this.values.operatingHours > 0) {
      // could be undefined in some cases?
      r =
        this.result.result *
        Calculator.getFrequencyNumber(this.values.paymentForm);
      this.result.hours = r / this.values.operatingHours;
      this.result.hours = this.result.hours < 0 ? 0 : this.result.hours;

      if (this.settings.isLeasing) {
        // @todo this should not happen if we'd pass the correct data (do not use ts-ignore!)
        // in some cases, this was undefined.
        if (typeof this.variables.balloon !== 'number') {
          this.variables.balloon = this.variablesDefault.balloon;
        }
        this.result.hoursBalloon =
          (r * 3 + this.variables.balloon) / this.values.operatingHours;
      }
    } else {
      this.result.hours = 0;
      this.result.hoursBalloon = 0;
    }
  }

  /**
   * Frequency string to number (in month)
   *
   * @param {string} frequency
   * @returns {number}
   */
  // eslint-disable-next-line consistent-return
  static getFrequencyNumber(frequency: string): number {
    switch (frequency) {
      case 'monthly':
        return 12;

      case 'quarterly':
        return 4;

      case 'semiannually':
        return 2;

      case 'annually':
        return 1;

      // no default
    }

    return 1;
  }

  /**
   * Format result object
   *
   * @param {boolean} allowAdvancePayment Enable advance payment logic?
   * @returns {ICalculatorResult}
   */
  formatResult(allowAdvancePayment: boolean): ICalculatorResult {
    this.result.rate = this.variables.rate;

    // convert negative zero to zero without sign
    if (this.result.APR === 0) {
      this.result.APR = 0;
    }

    this.result.residualValue = this.values.residualValue;

    this.result.amount = this.variables.amount;
    this.result.result = this.result.result < 0 ? 0 : this.result.result;

    if (allowAdvancePayment && this.values.advancePayments) {
      this.result.advancePayment =
        this.result.result * this.values.advancePayments;
      if (this.values.followingPayments) {
        this.result.paymentsNumber = this.values.followingPayments;
      }
    } else {
      this.result.paymentsNumber = this.variables.paymentsNumber;
    }
    this.result.downpayment = this.variables.downpayment;

    return this.result;
  }

  /**
   * result getter
   *
   * @returns {ICalculatorResult}
   */
  getResult(): ICalculatorResult {
    return this.result;
  }

  /*
   * PMT function found on Gist : https://gist.github.com/pies/4166888
   * rate   - interest rate per month
   * nper   - number of periods (months)
   * pv   - present value
   * fv   - future value
   * type - when the payments are due:
   *        0: end of the period, e.g. end of month (default)
   *        1: beginning of period
   */
  static PMT(
    rate: number,
    nper: number,
    pv: number,
    fv: number,
    type: number,
  ): number {
    // note: mostly unchanged / untouched to prevent issues

    // eslint-disable-next-line no-param-reassign
    if (!fv) fv = 0;
    // eslint-disable-next-line no-param-reassign
    if (!type) type = 0;

    // eslint-disable-next-line eqeqeq
    if (rate == 0) return -(pv + fv) / nper;

    // eslint-disable-next-line no-restricted-properties
    const pvif = Math.pow(1 + rate, nper);
    let pmt = (rate / (pvif - 1)) * -(pv * pvif + fv);

    // eslint-disable-next-line eqeqeq
    if (type == 1) {
      pmt /= 1 + rate;
    }

    return pmt;
  }

  /*
   * RATE function found on Gist: https://gist.github.com/ghalimi/4639009
   */
  static RATE(
    nper: number,
    payment: number,
    pv: number,
    fv: number,
    type: number,
    guess?: number,
  ): number {
    // Copyright (c) 2012 Sutoiku, Inc. (MIT License)
    // Credits: rabugento

    // note: mostly unchanged / untouched to prevent issues

    // eslint-disable-next-line no-param-reassign
    guess = guess === undefined ? 0.01 : guess;
    // eslint-disable-next-line no-param-reassign
    fv = fv === undefined ? 0 : fv;
    // eslint-disable-next-line no-param-reassign
    type = type === undefined ? 0 : type;

    // Set maximum epsilon for end of iteration
    const epsMax = 1e-10;

    // Set maximum number of iterations
    const iterMax = 10;

    // Implement Newton's method
    // eslint-disable-next-line one-var
    let y,
      y0,
      y1,
      x0,
      x1,
      i,
      f = 0;
    let rate = guess;
    if (Math.abs(rate) < epsMax) {
      y = pv * (1 + nper * rate) + payment * (1 + rate * type) * nper + fv;
    } else {
      f = Math.exp(nper * Math.log(1 + rate));
      y = pv * f + payment * (1 / rate + type) * (f - 1) + fv;
    }
    y0 = pv + payment * nper + fv;
    y1 = pv * f + payment * (1 / rate + type) * (f - 1) + fv;
    // eslint-disable-next-line no-multi-assign
    i = x0 = 0;
    x1 = rate;
    while (Math.abs(y0 - y1) > epsMax && i < iterMax) {
      rate = (y1 * x0 - y0 * x1) / (y1 - y0);
      x0 = x1;
      x1 = rate;
      if (Math.abs(rate) < epsMax) {
        y = pv * (1 + nper * rate) + payment * (1 + rate * type) * nper + fv;
      } else {
        f = Math.exp(nper * Math.log(1 + rate));
        y = pv * f + payment * (1 / rate + type) * (f - 1) + fv;
      }
      y0 = y1;
      y1 = y;
      // eslint-disable-next-line no-plusplus
      ++i;
    }
    return rate;
  }
}
